/**
 * Auth — real bcrypt+JWT against the Thren backend.
 *
 * Replaces the v1 localStorage stub (and the earlier B2C/MSAL attempt). The
 * backend at `/api/auth/*` hashes passwords with bcrypt and issues short JWTs
 * we cache in localStorage under `thren.session.v2`. Every API call in
 * `lib/api.ts` attaches the cached token as `Authorization: Bearer <jwt>`.
 *
 *   storage key   value
 *   ───────────   ─────────────────────────────────────────────────────────
 *   thren.session.v2   { token, user, savedAt } — see SessionEnvelope below
 *   thren.learn.session.v1   (legacy stub — wiped on first load, never read)
 *
 * Public surface preserved from previous versions:
 *   currentUser, isAuthenticated, signOut
 * New surface introduced for the bcrypt+JWT flow:
 *   signIn, signUp, getAccessToken, refreshUser, requestPasswordReset,
 *   resetPassword
 *
 * The User shape is also re-exported under its historical alias `AuthUser`
 * so existing imports (`CourseCard`, etc.) keep compiling without edits.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface User {
  /** Stable identifier issued by the backend (UUID or similar). */
  id: string;
  email: string;
  /** Display name — backend column is `displayName`; mapped on read. */
  name: string;
  /** True once the user has clicked the verification link. */
  emailVerified: boolean;
  /**
   * Subscription tier as known to the backend (billing.plan). With the
   * single-tier "Thren Access" model, `'access'` is the new canonical
   * paid value. The legacy values (`'starter' | 'all_access' | 'teams'`)
   * are kept in the union so existing rows in the DB still parse cleanly
   * — `isPaidPlan()` treats every non-`'free'` value as paid. Mirrored in
   * `BillingMeResponse.plan` in lib/api.ts.
   */
  plan: 'free' | 'access' | 'starter' | 'all_access' | 'teams';
  avatarUrl: string;
  /** ISO timestamp of the most recent successful sign-in. */
  signedInAt: string;
}

/** Back-compat alias — older imports reference `AuthUser`. */
export type AuthUser = User;

/**
 * Numeric rank for the backend's subscription tiers. Higher = more access.
 * With the single-tier model only `'free'` (0) and the paid tier (1) matter
 * — the legacy values map to the same rank as `'access'` so existing rows
 * (that haven't been migrated by the webhook yet) keep their entitlements.
 */
export const USER_PLAN_RANK: Record<User['plan'], number> = {
  free: 0,
  access: 1,
  starter: 1,
  all_access: 1,
  teams: 1,
};

/** True for any paid tier (anything above `free`). */
export function isPaidPlan(plan: User['plan'] | undefined | null): boolean {
  return !!plan && plan !== 'free';
}

export type AuthError =
  | 'invalid_credentials'
  | 'email_taken'
  | 'weak_password'
  | 'invalid_email'
  | 'network';

export type AuthResult =
  | { ok: true; user: User }
  | { ok: false; error: AuthError; message?: string };

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'thren.session.v2';
const LEGACY_STORAGE_KEY = 'thren.learn.session.v1';

interface SessionEnvelope {
  token: string;
  user: User;
  savedAt: string;
}

/**
 * Wire shape the backend hands back. We don't trust unknown extra fields, and
 * we coerce a couple of nullable bits into the strict User shape the UI uses.
 */
interface WireUser {
  id: string;
  email: string;
  displayName?: string;
  name?: string;
  emailVerified?: boolean;
  plan?: string;
  avatarUrl?: string | null;
}

interface WireAuthSuccess {
  token: string;
  user: WireUser;
}

/**
 * Project the backend's user payload into the strict UI shape. Missing fields
 * collapse to sensible defaults so the rest of the app never has to null-check.
 */
function normaliseUser(wire: WireUser): User {
  const planRaw = wire.plan ?? 'free';
  const plan: User['plan'] =
    planRaw === 'access' ||
    planRaw === 'starter' ||
    planRaw === 'all_access' ||
    planRaw === 'teams'
      ? planRaw
      : 'free';
  return {
    id: wire.id,
    email: wire.email,
    name: wire.displayName ?? wire.name ?? wire.email,
    emailVerified: Boolean(wire.emailVerified),
    plan,
    avatarUrl: wire.avatarUrl ?? '',
    signedInAt: new Date().toISOString(),
  };
}

/**
 * Migration: the v1 stub stored fake users that have no real credentials on
 * the backend. We wipe the key the first time this module loads in a tab so
 * stale data can't confuse the new flow. Idempotent — safe to run repeatedly.
 */
function migrateLegacy(): void {
  try {
    if (localStorage.getItem(LEGACY_STORAGE_KEY) !== null) {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
  } catch {
    /* private-mode / quota — nothing we can do, fall through. */
  }
}

let migrated = false;
function ensureMigrated(): void {
  if (migrated) return;
  migrated = true;
  migrateLegacy();
}

function readSession(): SessionEnvelope | null {
  ensureMigrated();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SessionEnvelope> | null;
    if (!parsed || typeof parsed.token !== 'string' || !parsed.user) return null;
    return parsed as SessionEnvelope;
  } catch {
    return null;
  }
}

function writeSession(token: string, user: User): SessionEnvelope {
  const env: SessionEnvelope = { token, user, savedAt: new Date().toISOString() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(env));
  } catch {
    /* storage failure — caller still gets a valid in-memory result. */
  }
  return env;
}

function clearSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Auth-required redirect plumbing (consumed by api.ts on 401)
// ---------------------------------------------------------------------------

/**
 * Called by `api.ts` when any /api/* request returns 401. We blow away the
 * stored session and bounce to /login with a `next` param so the user lands
 * back where they were after re-authenticating. Guarded against redirect
 * loops on the auth pages themselves.
 */
export function handleUnauthorized(): void {
  clearSession();
  if (typeof window === 'undefined') return;
  const current = window.location.pathname + window.location.search;
  // Don't loop: if we're already on an auth-gate page, just stay put.
  const onAuthPage = /^\/(login|signup|forgot-password|reset-password|verify-email)\b/.test(
    window.location.pathname,
  );
  if (onAuthPage) return;
  const next = encodeURIComponent(current);
  window.location.assign(`/login?next=${next}`);
}

// ---------------------------------------------------------------------------
// Backend helpers
// ---------------------------------------------------------------------------

const API_BASE = (import.meta.env.VITE_API_BASE ?? '/api') as string;

/**
 * POST a JSON body to one of the auth endpoints. Returns the parsed body and
 * the status so callers can branch on the documented error codes (400/401/409).
 * Network failures throw — `signIn` / `signUp` convert that into a typed
 * `AuthError = 'network'`.
 */
async function postJson<T>(
  path: string,
  body: unknown,
): Promise<{ status: number; body: T | { error?: string } }> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });
  let parsed: unknown = null;
  try {
    parsed = await res.json();
  } catch {
    parsed = {};
  }
  return { status: res.status, body: parsed as T | { error?: string } };
}

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

export function currentUser(): User | null {
  return readSession()?.user ?? null;
}

export function isAuthenticated(): boolean {
  return readSession() !== null;
}

export function signOut(): void {
  clearSession();
}

/** Used synchronously by `api.ts` to set the Authorization header. */
export function getAccessToken(): string | null {
  return readSession()?.token ?? null;
}

/**
 * Sign in with email + password. Maps the backend's wire errors to the typed
 * `AuthError` codes the UI switches on. Never throws on expected failures
 * (bad password, network) — only programming bugs propagate.
 */
export async function signIn(email: string, password: string): Promise<AuthResult> {
  try {
    const { status, body } = await postJson<WireAuthSuccess>('/auth/signin', {
      email,
      password,
    });
    if (status === 200 && 'token' in body && body.token) {
      const user = normaliseUser(body.user);
      writeSession(body.token, user);
      return { ok: true, user };
    }
    if (status === 401) {
      return { ok: false, error: 'invalid_credentials' };
    }
    const msg = 'error' in body && typeof body.error === 'string' ? body.error : undefined;
    return { ok: false, error: 'invalid_credentials', message: msg };
  } catch (err) {
    return {
      ok: false,
      error: 'network',
      message: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Sign up with email + password + display name. Backend returns:
 *   400 { error: 'weak_password' | 'invalid_email' }
 *   409 { error: 'email_taken' }
 *   200 { token, user }
 */
export async function signUp(
  email: string,
  password: string,
  displayName: string,
): Promise<AuthResult> {
  try {
    const { status, body } = await postJson<WireAuthSuccess>('/auth/signup', {
      email,
      password,
      displayName,
    });
    if (status === 200 && 'token' in body && body.token) {
      const user = normaliseUser(body.user);
      writeSession(body.token, user);
      return { ok: true, user };
    }
    const errCode =
      'error' in body && typeof body.error === 'string' ? body.error : undefined;
    if (status === 409) {
      return { ok: false, error: 'email_taken' };
    }
    if (status === 400) {
      const mapped: AuthError =
        errCode === 'invalid_email'
          ? 'invalid_email'
          : errCode === 'weak_password'
            ? 'weak_password'
            : 'weak_password';
      return { ok: false, error: mapped, message: errCode };
    }
    return { ok: false, error: 'network', message: errCode };
  } catch (err) {
    return {
      ok: false,
      error: 'network',
      message: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Refresh the cached user from /api/auth/me. Used after returning from a
 * verification link, after a plan upgrade, etc. Silently no-ops when there's
 * no token; a 401 clears the session and routes back to /login via
 * `handleUnauthorized()`.
 */
export async function refreshUser(): Promise<void> {
  const token = getAccessToken();
  if (!token) return;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/me`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });
  } catch {
    // Network blip — keep the cached user; the next call will retry.
    return;
  }
  if (res.status === 401) {
    handleUnauthorized();
    return;
  }
  if (!res.ok) return;
  try {
    const data = (await res.json()) as { user?: WireUser };
    if (!data.user) return;
    const fresh = normaliseUser(data.user);
    // Preserve the token we already have — /auth/me doesn't rotate it.
    writeSession(token, fresh);
  } catch {
    /* ignore — caller will re-try later. */
  }
}

/**
 * Kick off the password-reset flow. Backend always returns 200, even when the
 * email isn't registered, to avoid leaking user existence; we mirror that
 * here by collapsing errors into a silent resolve so the calling page can
 * unconditionally show "check your email".
 */
export async function requestPasswordReset(email: string): Promise<void> {
  try {
    await postJson('/auth/request-password-reset', { email });
  } catch {
    /* deliberately silent — UI shows confirmation either way. */
  }
}

/**
 * Complete the reset flow with the emailed token + a new password. Throws on
 * 400 (expired/invalid token, weak password) so the calling page can render
 * an inline error. 200 means the user can now sign in with the new password —
 * we don't auto-sign-them-in because the JWT issuance lives on /signin.
 */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  const { status, body } = await postJson<{ error?: string }>('/auth/reset-password', {
    token,
    newPassword,
  });
  if (status === 200) return;
  const message =
    body && 'error' in body && typeof body.error === 'string'
      ? body.error
      : `reset failed (${status})`;
  throw new Error(message);
}
